import React, { useState, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import AppLayout from '../../components/AppLayout';
import { COCKPIT_CSS } from '../cockpit/cockpitStyles';
import { CRM_CSS } from './crmStyles';
import { useCockpitDb } from '../cockpit/CockpitDataContext';
import { toast } from '../../hooks/use-toast';
import {
  TYPES_ACTION, comptes, compteOf, entitesOf, equipesOf,
  ficheOf, emptyFiche, actionsOf, daysSince, daysToBirthday,
  initials, fullName, saveFiche, removeFiche, addAction, removeAction,
} from './crmData';

const notify = (msg) => toast({ title: msg });
const today = () => new Date().toISOString().slice(0, 10);

// ─────────────────────────────────────────────────────────────────────────────
// Fiche client — la « cartographie intelligente » d'un contact rattaché à une
// équipe : identité, activité, sujets humains, et l'historique daté des
// actions menées (appel / mail / RDV / note) qui alimente les relances.
// ─────────────────────────────────────────────────────────────────────────────

export default function CrmFiche() {
  const { db, update } = useCockpitDb();
  const navigate = useNavigate();
  const { id } = useParams();
  const [sp] = useSearchParams();

  const isNew = id === 'new';
  const existing = !isNew && db ? ficheOf(db, Number(id)) : null;

  const [f, setF] = useState(() =>
    existing || emptyFiche(sp.get('account') || '', sp.get('entity') || '', sp.get('team') || '')
  );
  const [hobbyInput, setHobbyInput] = useState('');
  const [missionInput, setMissionInput] = useState('');
  const [log, setLog] = useState({ type: 'Appel', date: today(), note: '' });

  const compte = db ? compteOf(db, f.account) : null;
  const entites = entitesOf(compte);
  const equipes = useMemo(() => {
    const e = entites.find((x) => x.name === f.entity);
    return e ? equipesOf(e) : [];
  }, [entites, f.entity]);

  const actions = db && existing ? actionsOf(db, existing.id) : [];
  const last = actions[0];
  const dLast = last ? daysSince(last.date) : null;
  const dAnniv = daysToBirthday(f.anniversaire);

  if (!db) return null;

  if (!isNew && !existing) {
    return (
      <AppLayout title="CRM · Fiche client">
        <style>{COCKPIT_CSS}</style>
        <style>{CRM_CSS}</style>
        <div className="ckpt">
          <div className="ckpt-empty">
            <div className="ic">🔍</div>
            <div className="t">Fiche introuvable</div>
            <div className="d">Cette fiche client a été supprimée ou n'existe pas.</div>
            <button className="btn btn-primary" onClick={() => navigate('/crm/comptes')}>Retour aux comptes</button>
          </div>
        </div>
      </AppLayout>
    );
  }

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const onSave = () => {
    if (!f.prenom.trim() && !f.nom.trim()) { notify('Renseignez au moins un nom'); return; }
    if (!f.account || !f.entity || !f.team) { notify('Rattachez la fiche à une équipe'); return; }
    let newId = f.id;
    update((n) => { newId = saveFiche(n, f); });
    notify(isNew ? 'Fiche client créée' : 'Fiche enregistrée');
    if (isNew) navigate(`/crm/fiches/${newId}`, { replace: true });
  };

  const onDelete = () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Supprimer la fiche de ${fullName(f)} ?\n\nSon historique d'actions sera également supprimé.`)) return;
    update((n) => removeFiche(n, existing.id));
    notify('Fiche supprimée');
    navigate(`/crm/perimetre/${encodeURIComponent(f.account)}`);
  };

  const onAddAction = () => {
    if (!existing) { notify("Enregistrez d'abord la fiche"); return; }
    update((n) => addAction(n, existing.id, log));
    setLog({ type: 'Appel', date: today(), note: '' });
    notify('Action enregistrée');
  };

  const addHobby = () => {
    const v = hobbyInput.trim();
    if (!v || (f.hobbies || []).includes(v)) { setHobbyInput(''); return; }
    set('hobbies', [...(f.hobbies || []), v]);
    setHobbyInput('');
  };

  const addMission = () => {
    const v = missionInput.trim();
    if (!v || (f.missions || []).includes(v)) { setMissionInput(''); return; }
    set('missions', [...(f.missions || []), v]);
    setMissionInput('');
  };

  return (
    <AppLayout title="CRM · Fiche client">
      <style>{COCKPIT_CSS}</style>
      <style>{CRM_CSS}</style>
      <div className="ckpt">

        {/* En-tête identité */}
        <div className="crm-fiche-head">
          <span className="crm-av crm-av-lg">{initials(fullName(f))}</span>
          <div className="crm-fiche-id">
            <div className="crm-fiche-title">
              {fullName(f)}
              {f.decisionnaire && <span className="crm-deci" title="Décisionnaire">★</span>}
            </div>
            <div className="crm-fiche-sub">
              {[f.fonction, f.team, f.entity, f.account].filter(Boolean).join(' · ') || 'Non rattachée'}
            </div>
          </div>
          <span className="sp" />
          {existing && (
            <span className={`crm-pill ${dLast === null || dLast >= 30 ? 'crm-pill-warn' : 'crm-pill-ok'}`}>
              {dLast === null ? 'Jamais contacté' : dLast === 0 ? "Contacté aujourd'hui" : `Dernier contact il y a ${dLast} j`}
            </span>
          )}
        </div>

        {dAnniv !== null && dAnniv <= 30 && (
          <div className="crm-banner">
            🎂 Anniversaire {dAnniv === 0 ? "aujourd'hui" : `dans ${dAnniv} jour(s)`} — une occasion de reprendre contact.
          </div>
        )}

        <div className="crm-cols">
          {/* ── Colonne gauche : identité & rattachement ── */}
          <div className="card crm-card">
            <div className="crm-card-t">Identité</div>
            <div className="crm-grid2">
              <label className="crm-field"><span>Prénom</span>
                <input className="crm-input" value={f.prenom} onChange={(e) => set('prenom', e.target.value)} />
              </label>
              <label className="crm-field"><span>Nom</span>
                <input className="crm-input" value={f.nom} onChange={(e) => set('nom', e.target.value)} />
              </label>
              <label className="crm-field"><span>Email</span>
                <input className="crm-input" type="email" value={f.email} onChange={(e) => set('email', e.target.value)} />
              </label>
              <label className="crm-field"><span>Téléphone</span>
                <input className="crm-input" value={f.phone} onChange={(e) => set('phone', e.target.value)} />
              </label>
              <label className="crm-field crm-field-wide"><span>Fonction</span>
                <input className="crm-input" value={f.fonction} onChange={(e) => set('fonction', e.target.value)} />
              </label>
              <label className="crm-field crm-field-wide"><span>LinkedIn</span>
                <input className="crm-input" placeholder="https://linkedin.com/in/…" value={f.linkedin} onChange={(e) => set('linkedin', e.target.value)} />
              </label>
              <label className="crm-field"><span>Anniversaire</span>
                <input className="crm-input" type="date" value={f.anniversaire || ''} onChange={(e) => set('anniversaire', e.target.value)} />
              </label>
              <label className="crm-field crm-check">
                <input type="checkbox" checked={!!f.decisionnaire} onChange={(e) => set('decisionnaire', e.target.checked)} />
                <span>Décisionnaire</span>
              </label>
            </div>

            <div className="crm-card-t crm-card-t2">Rattachement</div>
            <div className="crm-grid2">
              <label className="crm-field"><span>Compte</span>
                <select className="crm-select" value={f.account}
                  onChange={(e) => setF((s) => ({ ...s, account: e.target.value, entity: '', team: '' }))}>
                  <option value="">—</option>
                  {comptes(db).map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
                </select>
              </label>
              <label className="crm-field"><span>Entité</span>
                <select className="crm-select" value={f.entity} disabled={!f.account}
                  onChange={(e) => setF((s) => ({ ...s, entity: e.target.value, team: '' }))}>
                  <option value="">—</option>
                  {entites.map((e) => <option key={e.name} value={e.name}>{e.name}</option>)}
                </select>
              </label>
              <label className="crm-field crm-field-wide"><span>Équipe</span>
                <select className="crm-select" value={f.team} disabled={!f.entity}
                  onChange={(e) => set('team', e.target.value)}>
                  <option value="">—</option>
                  {equipes.map((eq) => <option key={eq.name} value={eq.name}>{eq.name}</option>)}
                </select>
              </label>
            </div>
          </div>

          {/* ── Colonne droite : activité, sujets humains, missions ── */}
          <div className="card crm-card">
            <div className="crm-card-t">Activité & contexte</div>
            <label className="crm-field crm-field-wide"><span>Détails de l'activité</span>
              <textarea className="crm-input crm-textarea" rows={4} value={f.activite}
                onChange={(e) => set('activite', e.target.value)}
                placeholder="Ce dont l'équipe a la charge, ses enjeux, son organisation…" />
            </label>

            <div className="crm-card-t crm-card-t2">Sujets humains / hobbies</div>
            <div className="crm-chipline">
              {(f.hobbies || []).map((h) => (
                <span className="crm-tag" key={h}>
                  {h}
                  <button onClick={() => set('hobbies', f.hobbies.filter((x) => x !== h))} aria-label={`Retirer ${h}`}>✕</button>
                </span>
              ))}
            </div>
            <div className="crm-addrow">
              <input className="crm-input" placeholder="Ex. marathon, jazz, rugby…" value={hobbyInput}
                onChange={(e) => setHobbyInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addHobby())} />
              <button className="btn btn-ghost" onClick={addHobby}>Ajouter</button>
            </div>

            <div className="crm-card-t crm-card-t2">Missions placées chez ce client</div>
            <div className="crm-chipline">
              {(f.missions || []).map((m) => (
                <span className="crm-tag" key={m}>
                  {m}
                  <button onClick={() => set('missions', f.missions.filter((x) => x !== m))} aria-label={`Retirer ${m}`}>✕</button>
                </span>
              ))}
              {!(f.missions || []).length && <span className="crm-team-empty">Aucune mission enregistrée.</span>}
            </div>
            <div className="crm-addrow">
              <input className="crm-input" placeholder="Ex. Jean D. — Dev Java (2026)" value={missionInput}
                onChange={(e) => setMissionInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addMission())} />
              <button className="btn btn-ghost" onClick={addMission}>Ajouter</button>
            </div>
          </div>
        </div>

        {/* ── Actions menées ── */}
        <div className="card crm-card">
          <div className="crm-card-t">Actions menées</div>
          {!existing && <div className="crm-team-empty">Enregistrez la fiche pour commencer à journaliser vos actions.</div>}

          {existing && (
            <>
              <div className="crm-logrow">
                <select className="crm-select" value={log.type} onChange={(e) => setLog((s) => ({ ...s, type: e.target.value }))}>
                  {TYPES_ACTION.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                <input className="crm-input" type="date" value={log.date} onChange={(e) => setLog((s) => ({ ...s, date: e.target.value }))} />
                <input className="crm-input crm-log-note" placeholder="Note (optionnel)" value={log.note}
                  onChange={(e) => setLog((s) => ({ ...s, note: e.target.value }))}
                  onKeyDown={(e) => e.key === 'Enter' && onAddAction()} />
                <button className="btn btn-primary" onClick={onAddAction}>Enregistrer l'action</button>
              </div>

              {actions.length ? (
                <div className="crm-timeline">
                  {actions.map((a) => (
                    <div className="crm-tl-row" key={a.id}>
                      <span className="crm-tl-type">{a.type}</span>
                      <span className="crm-tl-date">{a.date}</span>
                      <span className="crm-tl-note">{a.note || <em>—</em>}</span>
                      <span className="sp" />
                      <button className="btn btn-ghost btn-danger btn-sm" onClick={() => { update((n) => removeAction(n, a.id)); notify('Action supprimée'); }}>✕</button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="crm-team-empty">Aucune action enregistrée pour l'instant.</div>
              )}
            </>
          )}
        </div>

        <div className="crm-footbar">
          <button className="btn btn-ghost" onClick={() => navigate(f.account ? `/crm/perimetre/${encodeURIComponent(f.account)}` : '/crm/comptes')}>
            ← Retour
          </button>
          <span className="sp" />
          {existing && <button className="btn btn-ghost btn-danger" onClick={onDelete}>Supprimer la fiche</button>}
          <button className="btn btn-primary" onClick={onSave}>{isNew ? 'Créer la fiche' : 'Enregistrer'}</button>
        </div>
      </div>
    </AppLayout>
  );
}
